import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import {
  AdminListAgenciesQuery,
  AgencyAdminOrderByOption,
  Order,
} from '@clipr/lib';

import { Store } from '../../store/store';
import { StoreNode } from '../../store';

export type AdminAgencySummary = AdminListAgenciesQuery['adminListAgencies']['edges'][number]['node'];

// Match backend AGENCY_AGGREGATION_PAGE_SIZE so the frontend doesn't silently
// drop agencies 101-200 that the ES composite agg returned.
const PAGE_SIZE = 200;

export class AdminAgenciesPageState extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);
  }

  @observable agencies: AdminAgencySummary[] = [];
  @observable isLoading = false;
  @observable totalCount: number | null = null;
  @observable error: string | null = null;
  @observable search = '';
  @observable sortField: AgencyAdminOrderByOption = AgencyAdminOrderByOption.AgencyName;
  @observable sortOrder: Order = Order.Asc;
  // True when the backend reports hasNextPage = aggregation hit its cap. UI
  // surfaces a warning so the operator knows the list may be truncated.
  @observable resultsTruncated = false;

  @computed
  get hasResults(): boolean {
    return this.agencies.length > 0;
  }

  @action
  async pageMounted() {
    await this.fetch();
  }

  @action
  pageUnmounted() {
    this.error = null;
  }

  @action
  setSearch(value: string) {
    this.search = value;
  }

  @action
  setSort(field: AgencyAdminOrderByOption) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === Order.Asc ? Order.Desc : Order.Asc;
    } else {
      this.sortField = field;
      this.sortOrder = Order.Asc;
    }
    void this.fetch();
  }

  @action
  async applySearch() {
    await this.fetch();
  }

  @action
  async fetch() {
    this.isLoading = true;
    this.error = null;

    const trimmed = this.search.trim();
    const [listRes, listErr] = await this.store.apiService.adminListAgencies({
      first: PAGE_SIZE,
      filter: trimmed ? { _search: trimmed } : undefined,
      sort: { field: this.sortField, order: this.sortOrder },
    });
    // Sponsor-only signups never appear in the team-index aggregation; fetch
    // them separately and fold them in.
    const [zeroLibRes, zeroLibErr] = await this.store.apiService.adminListAgenciesWithoutLibraries();

    runInAction(() => {
      this.isLoading = false;
      if (listErr) {
        this.error = listErr.message;
        return;
      }
      const conn = listRes?.adminListAgencies;
      const withLibs: AdminAgencySummary[] = (conn?.edges ?? []).map((edge) => edge.node);

      const withoutLibs: AdminAgencySummary[] = !zeroLibErr
        ? (zeroLibRes?.adminListAgenciesWithoutLibraries ?? [])
        : [];

      // adminListAgencies wins on overlap (it has the richer aggregate fields).
      const seen = new Set(withLibs.map((a) => a.orgId));
      const filteredZeroLib = trimmed
        ? withoutLibs.filter((a) => {
          const q = trimmed.toLowerCase();
          return a.agencyName.toLowerCase().includes(q)
            || a.agencyDomain.toLowerCase().includes(q)
            || (a.sponsorEmail ?? '').toLowerCase().includes(q);
        })
        : withoutLibs;
      const merged = [...withLibs, ...filteredZeroLib.filter((a) => !seen.has(a.orgId))];

      this.agencies = merged;
      this.totalCount = merged.length;
      this.resultsTruncated = !!conn?.pageInfo.hasNextPage;
    });
  }
}
